import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Card,
  Tabs,
  Tab,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import "./AncillaryServices.css";
import { makeStyles } from "@material-ui/core/styles";
import LuggageIcon from "@material-ui/icons/LocalMall";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import SeatIcon from "@material-ui/icons/AirlineSeatReclineNormal";
import PersonIcon from "@material-ui/icons/Person";
import FlightTakeoffIcon from "@material-ui/icons/FlightTakeoff";
import FlightLandIcon from "@material-ui/icons/FlightLand";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1.5rem",
    marginBottom: "1rem",
    backgroundColor: "var(--white)",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "1rem",
    "& svg": {
      color: "var(--primary)",
    },
  },
  serviceContainer: {
    backgroundColor: "#f5f5f5",
    padding: "1.5rem",
    borderRadius: "8px",
    marginBottom: "1rem",
    position: "relative",
  },
  formControl: {
    width: "100%",
    "& .MuiSelect-root": {
      backgroundColor: "white",
      padding: "0.75rem 1rem",
      borderRadius: "4px",
      border: "1px solid #c1c7cd",
    },
  },
  selectLabel: {
    fontWeight: 600,
    marginBottom: "0.5rem",
  },
  routeInfo: {
    backgroundColor: "#f8f9fa",
    padding: "0.5rem 1rem",
    borderRadius: "4px",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  tabs: {
    marginBottom: "1.5rem",
    borderBottom: "1px solid #e0e0e0",
    "& .MuiTab-root": {
      textTransform: "none",
      minWidth: "150px",
      fontWeight: 600,
      fontSize: "1rem",
    },
    "& .Mui-selected": {
      color: "var(--primary)",
    },
  },
  tabPanel: {
    padding: "1.5rem 0",
  },
  tabIcon: {
    marginRight: "8px",
    fontSize: "1.2rem",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderRadius: "8px",
  },
  serviceSection: {
    marginBottom: "1.5rem",
  },
  serviceSectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "1rem",
    "& svg": {
      color: "var(--primary)",
    },
  },
  flightSegmentSelector: {
    marginBottom: "1.5rem",
    display: "flex",
    justifyContent: "center",
    "& .MuiToggleButtonGroup-root": {
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
    },
    "& .MuiToggleButton-root": {
      padding: "10px 20px",
      fontWeight: 600,
      "&.Mui-selected": {
        backgroundColor: "var(--primary)",
        color: "white",
        "&:hover": {
          backgroundColor: "var(--primary-dark)",
        },
      },
    },
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`passenger-tabpanel-${index}`}
      aria-labelledby={`passenger-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const AncillaryServices = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [flightType, setFlightType] = useState("outbound");

  const ancillaryServices = useSelector(
    (state) => state.flight.ancillaryServices
  );
  const selectedFlight = useSelector((state) => state.flight.selectedFlight);
  const ssrData = useSelector(
    (state) => state.flight.ancillaryServices.ssrData
  );
  const passengers = useSelector((state) => state.flight.passengers);

  // Check if it's a round trip by looking at the return data
  const isRoundTrip =
    ssrData?.Baggage?.return?.length > 0 ||
    ssrData?.MealDynamic?.return?.length > 0 ||
    selectedFlight?.JourneyType === "Return" ||
    selectedFlight?.journey_type === "Return";

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFlightTypeChange = (event, newFlightType) => {
    if (newFlightType !== null) {
      console.log(
        `[DEBUG] Switching flight segment from ${flightType} to ${newFlightType}`
      );

      // Store current selections before switching
      const currentSelections = {
        baggage: { ...ancillaryServices.baggage },
        meals: { ...ancillaryServices.meals },
      };
      console.log(
        `[DEBUG] Current selections before switch:`,
        currentSelections
      );

      setFlightType(newFlightType);
    }
  };

  useEffect(() => {
    // Log flight and round-trip status when component mounts or when selectedFlight changes
    console.log("Selected Flight:", selectedFlight);
    console.log("Round Trip Status:", isRoundTrip);

    // Clear previous selections when component mounts
    dispatch({ type: "CLEAR_ANCILLARY_SERVICES" });

    const fetchSSRData = async () => {
      if (!selectedFlight?.TraceId || !selectedFlight?.ResultIndex || ssrData) {
        return;
      }

      try {
        setLoading(true);
        const fareQuoteResponse = await axios.post("/flights/farequote-tbo", {
          TraceId: selectedFlight.TraceId,
          ResultIndex: selectedFlight.ResultIndex,
        });

        dispatch({ type: "SET_FARE_QUOTE_DATA", data: fareQuoteResponse.data });
        dispatch({ type: "SET_FARE_QUOTE_SUCCESS", success: true });

        const ssrResponse = await axios.post("/flights/ssr-tbo", {
          TraceId: selectedFlight.TraceId,
          ResultIndex: selectedFlight.ResultIndex,
        });

        // Log the SSR data to debug
        console.log("SSR Response:", ssrResponse.data.data);

        // Fix return data structure if needed
        const ssrData = ssrResponse.data.data;

        // Ensure both outbound and return arrays exist for Baggage
        if (ssrData.Baggage && !ssrData.Baggage.outbound) {
          ssrData.Baggage.outbound = [];
        }
        if (ssrData.Baggage && !ssrData.Baggage.return) {
          ssrData.Baggage.return = [];
        }

        // Ensure both outbound and return arrays exist for MealDynamic
        if (ssrData.MealDynamic && !ssrData.MealDynamic.outbound) {
          ssrData.MealDynamic.outbound = [];
        }
        if (ssrData.MealDynamic && !ssrData.MealDynamic.return) {
          ssrData.MealDynamic.return = [];
        }

        // Log the fixed data structure
        console.log("Fixed SSR Data:", ssrData);
        console.log(
          "Round Trip? Baggage return length:",
          ssrData?.Baggage?.return?.length
        );
        console.log(
          "Round Trip? MealDynamic return length:",
          ssrData?.MealDynamic?.return?.length
        );

        dispatch({ type: "SET_SSR_DATA", data: ssrData });
      } catch (error) {
        console.error("Error fetching SSR data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSSRData();
  }, [selectedFlight]);

  const handleBaggageChange = (passengerId) => (event) => {
    const selectedValue = event.target.value;
    let baggageData = null;

    if (
      selectedValue !== "No Excess/Extra Baggage" &&
      ssrData?.Baggage?.[flightType]?.length
    ) {
      const option = ssrData.Baggage[flightType].find(
        (opt) => `${opt.Weight}kg` === selectedValue
      );
      if (option) {
        // Make sure we keep the original WayType from the API
        // For outbound WayType should be 1, for return WayType should be 2
        const wayType = flightType === "outbound" ? 1 : 2;

        baggageData = {
          AirlineCode: option.AirlineCode,
          Code: option.Code,
          Currency: option.Currency,
          Description: option.Description,
          Destination: option.Destination,
          FlightNumber: option.FlightNumber,
          Origin: option.Origin,
          Price: option.Price,
          WayType: wayType, // Set WayType based on flight type
          Weight: option.Weight,
          FlightType: flightType,
        };
      }
    }

    // Use passengerId and flightType as a composite key for baggage
    const baggageKey = `${passengerId}_${flightType}`;

    console.log(`[DEBUG] Baggage selection for ${flightType}:`, {
      passengerId,
      flightType,
      baggageKey,
      baggageData,
    });

    // Only update the specific flight type's baggage
    dispatch({
      type: "UPDATE_PASSENGER_BAGGAGE",
      passengerId,
      baggage: baggageData,
      flightType: flightType,
      baggageKey,
    });
  };

  const handleMealChange = (passengerId) => (event) => {
    const selectedValue = event.target.value;
    let mealData = null;

    if (
      selectedValue !== "NoMeal" &&
      ssrData?.MealDynamic?.[flightType]?.length
    ) {
      const option = ssrData.MealDynamic[flightType].find(
        (opt) => opt.Code === selectedValue
      );
      if (option) {
        // Make sure we keep the original WayType from the API
        // For outbound WayType should be 1, for return WayType should be 2
        const wayType = flightType === "outbound" ? 1 : 2;

        mealData = {
          AirlineCode: option.AirlineCode,
          AirlineDescription: option.AirlineDescription || "",
          Code: option.Code,
          Currency: option.Currency,
          Description: option.Description,
          Destination: option.Destination,
          FlightNumber: option.FlightNumber,
          Origin: option.Origin,
          Price: option.Price,
          Quantity: option.Quantity,
          WayType: wayType, // Set WayType based on flight type
          FlightType: flightType,
        };
      }
    }

    // Use passengerId and flightType as a composite key for meals
    const mealKey = `${passengerId}_${flightType}`;

    console.log(`[DEBUG] Meal selection for ${flightType}:`, {
      passengerId,
      flightType,
      mealKey,
      mealData,
    });

    // Only update the specific flight type's meal
    dispatch({
      type: "UPDATE_PASSENGER_MEAL",
      passengerId,
      meal: mealData,
      flightType: flightType,
      mealKey,
    });
  };

  const handleSeatSelect = () => {
    console.log("Seat selection clicked");
  };

  const renderPassengerServices = (passenger, index) => {
    const selectedBaggage = getBaggageSelection(passenger.id);
    const selectedMealValue = getMealSelection(passenger.id);

    return (
      <div className={classes.serviceContainer}>
        {isRoundTrip && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "1rem",
              padding: "8px",
              backgroundColor:
                flightType === "outbound" ? "#e3f2fd" : "#fff8e1",
              borderRadius: "4px",
            }}
          >
            {flightType === "outbound" ? (
              <FlightTakeoffIcon
                style={{ marginRight: "8px", color: "var(--primary)" }}
              />
            ) : (
              <FlightLandIcon
                style={{ marginRight: "8px", color: "#ff9800" }}
              />
            )}
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {flightType === "outbound" ? "Outbound Flight" : "Return Flight"}{" "}
              {getFlightInfo(flightType)}
            </Typography>
          </div>
        )}

        {/* Baggage Section */}
        <div className={classes.serviceSection}>
          <div className={classes.serviceSectionTitle}>
            <LuggageIcon />
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Baggage
            </Typography>
          </div>
          <FormControl className={classes.formControl}>
            <Typography className={classes.selectLabel}>
              Extra Baggage
            </Typography>
            <Select
              value={selectedBaggage}
              onChange={handleBaggageChange(passenger.id)}
              className={classes.selectInput}
              disabled={loading}
            >
              {renderBaggageOptions()}
            </Select>
          </FormControl>
        </div>

        {/* Meal Section */}
        <div className={classes.serviceSection}>
          <div className={classes.serviceSectionTitle}>
            <RestaurantIcon />
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Meal Preference
            </Typography>
          </div>
          <FormControl className={classes.formControl}>
            <Typography className={classes.selectLabel}>Select Meal</Typography>
            <Select
              value={selectedMealValue}
              onChange={handleMealChange(passenger.id)}
              className={classes.selectInput}
              disabled={loading}
            >
              {renderMealOptions()}
            </Select>
          </FormControl>
        </div>

        {/* Seat selection section removed */}

        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress />
          </div>
        )}
      </div>
    );
  };

  const getBaggageSelection = (passengerId) => {
    // First, check for the composite key that includes the flight type
    const baggageKey = `${passengerId}_${flightType}`;
    const passengerBaggage = ancillaryServices.baggage[baggageKey];

    // If no specific selection for this flight type, return default
    if (!passengerBaggage || !passengerBaggage.Code) {
      return "No Excess/Extra Baggage";
    }

    if (passengerBaggage.Code === "NoBaggage") {
      return "No Excess/Extra Baggage";
    }

    return `${passengerBaggage.Weight}kg`;
  };

  const getMealSelection = (passengerId) => {
    // First, check for the composite key that includes the flight type
    const mealKey = `${passengerId}_${flightType}`;
    const mealData = ancillaryServices.meals[mealKey];

    // If no specific selection for this flight type, return default
    if (!mealData || !mealData.Code) {
      return "NoMeal";
    }

    return mealData.Code;
  };

  // Debug log for tracking state
  useEffect(() => {
    console.log("Current flightType:", flightType);
    console.log("Current ancillary selections:", ancillaryServices);
  }, [flightType, ancillaryServices]);

  const renderBaggageOptions = () => {
    if (!ssrData?.Baggage?.[flightType]?.length) {
      return (
        <MenuItem value="No Excess/Extra Baggage">
          No Excess/Extra Baggage
        </MenuItem>
      );
    }

    return [
      <MenuItem value="No Excess/Extra Baggage" key="no-baggage">
        No Excess/Extra Baggage
      </MenuItem>,
      ...ssrData.Baggage[flightType].map((option, index) => {
        // Get flight details for display
        const flightDetails = option.FlightNumber
          ? `(${option.AirlineCode}-${option.FlightNumber})`
          : "";

        return (
          <MenuItem value={`${option.Weight}kg`} key={index}>
            {option.Weight}kg {flightDetails} - ₹{option.Price}
          </MenuItem>
        );
      }),
    ];
  };

  const renderMealOptions = () => {
    if (!ssrData?.MealDynamic?.[flightType]?.length) {
      return <MenuItem value="NoMeal">Add No Meal ₹0</MenuItem>;
    }

    return [
      <MenuItem value="NoMeal" key="no-meal">
        Add No Meal ₹0
      </MenuItem>,
      ...ssrData.MealDynamic[flightType].map((option, index) => {
        // Get flight details for display
        const flightDetails = option.FlightNumber
          ? `(${option.AirlineCode}-${option.FlightNumber})`
          : "";

        const displayText = `${
          option.Description || option.AirlineDescription || ""
        } ${flightDetails} - ₹${option.Price}`;
        return (
          <MenuItem value={option.Code} key={index}>
            {displayText}
          </MenuItem>
        );
      }),
    ];
  };

  // Prepare flight information for display
  const getFlightInfo = (type) => {
    if (
      !ssrData ||
      !ssrData.Baggage ||
      !ssrData.Baggage[type] ||
      ssrData.Baggage[type].length === 0
    ) {
      return "";
    }

    // Get the first flight's details
    const flight = ssrData.Baggage[type][0];
    if (flight && flight.FlightNumber) {
      return ` (${flight.Origin} → ${flight.Destination})`;
    }

    return "";
  };

  // Get flight numbers for display
  const getFlightNumber = (type) => {
    if (
      !ssrData ||
      !ssrData.Baggage ||
      !ssrData.Baggage[type] ||
      ssrData.Baggage[type].length === 0
    ) {
      return "";
    }

    // Get the first flight's details
    const flight = ssrData.Baggage[type][0];
    if (flight && flight.FlightNumber) {
      return `${flight.AirlineCode}-${flight.FlightNumber}`;
    }

    return "";
  };

  // Log detailed SSR data structure once available
  useEffect(() => {
    if (ssrData) {
      console.log("[DEBUG] SSR Data structure:", {
        Baggage: ssrData.Baggage,
        MealDynamic: ssrData.MealDynamic,
        outboundBaggageCount: ssrData?.Baggage?.outbound?.length || 0,
        returnBaggageCount: ssrData?.Baggage?.return?.length || 0,
        outboundMealCount: ssrData?.MealDynamic?.outbound?.length || 0,
        returnMealCount: ssrData?.MealDynamic?.return?.length || 0,
      });
    }
  }, [ssrData]);

  return (
    <Card className={classes.root}>
      <Typography variant="h6" style={{ marginBottom: "1.5rem" }}>
        Ancillary Services
      </Typography>

      {isRoundTrip && (
        <div className={classes.flightSegmentSelector}>
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Select Flight Segment
            </Typography>
          </div>
          <ToggleButtonGroup
            value={flightType}
            exclusive
            onChange={handleFlightTypeChange}
            aria-label="flight direction"
            color="primary"
            size="large"
            style={{ width: "100%", maxWidth: "600px" }}
          >
            <ToggleButton
              value="outbound"
              aria-label="outbound flight"
              style={{
                width: "50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "15px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <FlightTakeoffIcon
                  style={{
                    marginRight: "8px",
                    color:
                      flightType === "outbound" ? "white" : "var(--primary)",
                  }}
                />
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Outbound
                </Typography>
              </div>
              <Typography variant="body2">
                {getFlightNumber("outbound")}
              </Typography>
              <Typography variant="body2">
                {getFlightInfo("outbound")}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="return"
              aria-label="return flight"
              style={{
                width: "50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "15px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <FlightLandIcon
                  style={{
                    marginRight: "8px",
                    color: flightType === "return" ? "white" : "#ff9800",
                  }}
                />
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Return
                </Typography>
              </div>
              <Typography variant="body2">
                {getFlightNumber("return")}
              </Typography>
              <Typography variant="body2">{getFlightInfo("return")}</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        className={classes.tabs}
        variant="scrollable"
        scrollButtons="auto"
      >
        {passengers.map((passenger, index) => (
          <Tab
            key={passenger.id}
            icon={<PersonIcon className={classes.tabIcon} />}
            label={`${
              passenger.category.charAt(0).toUpperCase() +
              passenger.category.slice(1)
            } ${index + 1}`}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {passengers.map((passenger, index) => (
        <TabPanel key={passenger.id} value={tabValue} index={index}>
          {renderPassengerServices(passenger, index)}
        </TabPanel>
      ))}
    </Card>
  );
};

export default AncillaryServices;
